import React from 'react';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {DocSidebarPagePanels} from 'app/general/structures/DocSidebarPagePanels';
import {useModelChangeSubscription} from 'app/subscriptions';
import {useTaskTemplates} from 'app/tasks/api';
import {TaskTemplateItem} from 'app/tasks/components/TaskTemplateItem';
import {gt} from 'app/utils/i18n';

import {DetailPageRelatedTasksSidebarFooter} from './DetailPageRelatedTasksSidebarFooter';

import type {TaskAssignableContentTypes} from 'sweet-types';

interface Props {
  contentType: TaskAssignableContentTypes;
  objectId: number;
  toggleSidebar: () => unknown;
  onChange?: () => unknown;
  canAssign: boolean;
}

export function DetailPageRelatedTasksSidebar({
  contentType,
  objectId,
  toggleSidebar,
  onChange,
  canAssign,
}: Props): JSX.Element {
  const {timezone} = useCurrentUserState().data!;

  const {
    currentData: tasks,
    isLoading,
    refetch,
  } = useTaskTemplates({
    object_id: objectId,
    content_type: contentType,
  });

  useModelChangeSubscription(
    (message, {runWhenAllRelatedMatch}) => {
      runWhenAllRelatedMatch(
        message,
        'tasktemplate',
        {
          content_type: contentType,
          id: objectId,
        },
        fetchData
      );
    },
    [contentType, objectId]
  );

  function fetchData() {
    if (onChange != null) {
      onChange();
    }
    return Promise.resolve(refetch());
  }

  return (
    <DocSidebarPagePanels
      title={gt('Related Tasks')}
      onClose={toggleSidebar}
      loading={isLoading}
      footer={
        <DetailPageRelatedTasksSidebarFooter
          contentType={contentType}
          objectId={objectId}
          canAssign={canAssign}
        />
      }
    >
      {tasks?.count == 0 ? (
        <NoResultsMessage
          icon="fa-tasks"
          head={gt('No related tasks')}
          onSidebar={true}
        >
          {gt('Use the button below to assign this procedure as task')}
        </NoResultsMessage>
      ) : tasks != null ? (
        <ul className="list-group">
          {tasks.results.map((task) => (
            <li key={task.url} className="list-group-item p-0">
              <TaskTemplateItem task={task} userTimezone={timezone} />
            </li>
          ))}
        </ul>
      ) : null}
    </DocSidebarPagePanels>
  );
}
